import { useEffect } from "react";

import { useReplayStore, type ReplaySpeed } from "@/stores/replayStore";

const SPEEDS: ReplaySpeed[] = [0.5, 1, 2, 5, 10, 20, 50];

/**
 * Unified keyboard shortcut handler for Bar Replay active playback.
 *
 * Shortcuts:
 *   Space       — toggle play/pause; restart when ended
 *   + / =       — increase speed (no wrap)
 *   -           — decrease speed (no wrap)
 *   Home        — jump to replay start (pauses if playing)
 *   End         — jump to replay end (pauses if playing)
 *   ArrowRight  — step forward one candle (pauses if playing)
 *   ArrowLeft   — step backward one candle (pauses if playing)
 *
 * All shortcuts:
 *   - Only fire when replay is in active mode (ready/playing/paused/ended).
 *   - Suppressed when focus is in input, textarea, select, or contenteditable.
 *   - Call preventDefault() to suppress browser defaults.
 *   - Removed on cleanup.
 */
export function useReplayKeyboard() {
  const isActive = useReplayStore((state) => state.isActive);

  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const isArrow = event.key === "ArrowLeft" || event.key === "ArrowRight";

      // Always block arrow keys from reaching klinecharts, including on key-repeat.
      if (isArrow) {
        event.preventDefault();
        event.stopPropagation();
      } else if (event.repeat) {
        return;
      }

      const el = document.activeElement;
      const tag = el?.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        (el instanceof HTMLElement && el.isContentEditable)
      ) {
        return;
      }

      const state = useReplayStore.getState();
      if (!state.isActive) return;

      const activeStates = new Set(["ready", "playing", "paused", "ended"]);
      if (!activeStates.has(state.sessionState)) return;

      switch (event.key) {
        case " ": {
          event.preventDefault();
          if (state.sessionState === "ended") {
            if (state.startDate != null && state.endDate != null) {
              state.startPreflight(state.startDate, state.endDate);
            }
          } else {
            state.togglePause();
          }
          break;
        }

        case "+":
        case "=": {
          event.preventDefault();
          cycleSpeed(state.speed, 1, state.setSpeed);
          break;
        }

        case "-": {
          event.preventDefault();
          cycleSpeed(state.speed, -1, state.setSpeed);
          break;
        }

        case "Home": {
          event.preventDefault();
          if (state.sessionState === "playing") state.pause();
          state.jumpToStart();
          break;
        }

        case "End": {
          event.preventDefault();
          if (state.sessionState === "playing") state.pause();
          state.jumpToEnd();
          break;
        }

        case "ArrowRight": {
          if (state.sessionState === "playing") state.pause();
          state.stepForward();
          break;
        }

        case "ArrowLeft": {
          if (state.sessionState === "playing") state.pause();
          state.stepBackward();
          break;
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown, { capture: true });
    return () => document.removeEventListener("keydown", handleKeyDown, { capture: true });
  }, [isActive]);
}

function cycleSpeed(
  currentSpeed: ReplaySpeed,
  direction: 1 | -1,
  setSpeed: (s: ReplaySpeed) => void,
) {
  const idx = SPEEDS.indexOf(currentSpeed);
  if (idx === -1) return;
  const nextIdx = idx + direction;
  if (nextIdx < 0 || nextIdx >= SPEEDS.length) return;
  setSpeed(SPEEDS[nextIdx]);
}
